import { useEffect, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";
import { PromotionSelect } from "@/components/marketing/PromotionSelect";
import { SmsCostMeter } from "@/components/marketing/SmsCostMeter";
import { TestSendCard } from "@/components/marketing/TestSendCard";
import { IphonePreview } from "@/components/marketing/IphonePreview";
import KillSwitchBanner from "@/components/marketing/KillSwitchBanner";
import {
  filterBySelection,
  useAudienceBreakdown,
  useCreateCampaign,
  type AudienceSelection,
} from "@/hooks/sms/useCampaignLaunch";
import { useMarketingSettings } from "@/hooks/sms/useMarketingSettings";
import { useContactCounts } from "@/hooks/sms/useContactCounts";
import { calculateCostMeter, planSendCounts, sampleSendVars, type CostMeterSettings } from "@/utils/smsCostMeter";
import { renderTemplate } from "@/utils/smsShared";
import type { Promotion } from "@/types/sms";

interface CampaignComposerProps {
  storeId: string;
  promotions: Promotion[];
  initialPromotionId?: string | null;
  onBack: () => void;
}

/** The composer's audience chips: one per pickable group, each mapping onto
 *  the engine's AudienceSelection. Day ranges mirror RevenuePlays'
 *  SEGMENT_INFO / v_customer_lifecycle — keep in sync if those change. */
const AUDIENCE_CHIPS: { key: string; label: string; days: string | null; selection: AudienceSelection }[] = [
  { key: "everyone", label: "Everyone", days: null, selection: { audienceType: "everyone" } },
  { key: "active", label: "Active", days: "<30d", selection: { audienceType: "active" } },
  { key: "cooling", label: "Cooling", days: "30–60d", selection: { audienceType: "lurkers", lurkerSegment: "cooling" } },
  { key: "at_risk", label: "At risk", days: "60–90d", selection: { audienceType: "lurkers", lurkerSegment: "at_risk" } },
  { key: "lapsing", label: "Lapsing", days: "90–180d", selection: { audienceType: "lurkers", lurkerSegment: "lapsing" } },
];

function defaultMessageBody(promotion: Promotion | undefined): string {
  if (!promotion) return "";
  return `${promotion.title}: {offer_link}\nReply STOP to opt out.`;
}

const audienceLabelFor = (chipKey: string): string => {
  switch (chipKey) {
    case "everyone":
      return "Everyone (all opted-in subscribers)";
    case "active":
      return "Active customers (ordered in the last 30 days)";
    case "cooling":
      return "Cooling customers (30–60 days)";
    case "at_risk":
      return "At-risk customers (60–90 days)";
    default:
      return "Lapsing customers (90–180 days)";
  }
};

/**
 * The Campaign tab's send composer (captain-approved round-4 design): the
 * message with its cost meter directly beneath, live audience counts, a test
 * send to a typed number, and an iPhone-style preview of exactly how the text
 * lands. Creates a scheduled `marketing_campaigns` row; never writes
 * campaign_recipients (edge-only). The final create stays gated behind an
 * AlertDialog naming the promotion, audience, and estimated count.
 */
export function CampaignComposer({ storeId, promotions, initialPromotionId, onBack }: CampaignComposerProps) {
  const [promotionId, setPromotionId] = useState<string | null>(initialPromotionId ?? null);
  const [audienceKey, setAudienceKey] = useState<string>("everyone");
  const [holdoutPct, setHoldoutPct] = useState(10);
  const [sendMode, setSendMode] = useState<"immediate" | "scheduled">("immediate");
  const [scheduledAt, setScheduledAt] = useState("");
  const [messageBody, setMessageBody] = useState(() =>
    defaultMessageBody(promotions.find((p) => p.id === (initialPromotionId ?? null)) ?? undefined),
  );
  const [confirmOpen, setConfirmOpen] = useState(false);

  const promotion = promotions.find((p) => p.id === promotionId);
  const createCampaign = useCreateCampaign();
  const { data: marketingSettings } = useMarketingSettings();
  const { data: breakdown, isLoading: breakdownLoading } = useAudienceBreakdown();
  const { data: contactCounts } = useContactCounts();

  useEffect(() => {
    setMessageBody(defaultMessageBody(promotion));
    // Reset the draft only when the promotion changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotionId]);

  const chip = AUDIENCE_CHIPS.find((c) => c.key === audienceKey) ?? AUDIENCE_CHIPS[0];
  const selection = chip.selection;

  const selectedMembers = useMemo(
    () => (breakdown ? filterBySelection(breakdown.members, selection) : []),
    [breakdown, selection],
  );
  const [campaignId] = useState(() => crypto.randomUUID());
  const sendCounts = useMemo(
    () => planSendCounts(selectedMembers, campaignId, holdoutPct),
    [selectedMembers, campaignId, holdoutPct],
  );
  const audienceCount = sendCounts.audience;
  const textedCount = sendCounts.texted;
  const countsLine =
    sendCounts.heldOut > 0
      ? `${audienceCount.toLocaleString()} in audience · ${sendCounts.heldOut.toLocaleString()} held out to measure lift · ${textedCount.toLocaleString()} will be texted`
      : `${textedCount.toLocaleString()} customers will be texted`;

  const countFor = (key: string): number | null => {
    if (!breakdown) return null;
    const counts = breakdown.counts;
    if (key === "everyone") return counts.everyone;
    if (key === "active") return counts.active;
    if (key === "cooling") return counts.cooling;
    if (key === "at_risk") return counts.at_risk;
    return counts.lapsing;
  };

  const meterSettings: CostMeterSettings | null = marketingSettings
    ? {
        public_base_url: marketingSettings.public_base_url ?? "",
        store_name: marketingSettings.store_name ?? "",
        store_phone: marketingSettings.store_phone ?? null,
        store_address: marketingSettings.store_address ?? "",
        sms_cost_per_segment: Number(marketingSettings.sms_cost_per_segment ?? 0.01),
      }
    : null;

  const meter = meterSettings
    ? calculateCostMeter({
        messageBody,
        settings: meterSettings,
        audienceCount: textedCount,
        hasImage: !!promotion?.image_url,
        vars: promotion ? { promo_title: promotion.title } : undefined,
      })
    : null;

  const previewBody = meterSettings
    ? renderTemplate(messageBody, sampleSendVars(meterSettings, promotion ? { promo_title: promotion.title } : undefined))
    : messageBody;

  const audienceLabel = audienceLabelFor(audienceKey);
  const effectiveSendAt =
    sendMode === "immediate" || scheduledAt.length === 0 ? new Date().toISOString() : new Date(scheduledAt).toISOString();
  const canReview =
    !!promotion &&
    messageBody.trim().length > 0 &&
    (sendMode === "immediate" || scheduledAt.length > 0) &&
    holdoutPct >= 0 &&
    holdoutPct <= 100;

  const launch = async () => {
    if (!promotion) return;
    await createCampaign.mutateAsync({
      campaignId,
      storeId,
      name: `${promotion.title} — ${audienceLabel.split(" (")[0]} (${new Date().toLocaleDateString()})`,
      promotionId: promotion.id,
      messageBody,
      imageUrl: promotion.image_url,
      expirationDays: promotion.default_expiration_days ?? null,
      timerMinutes: promotion.default_timer_minutes ?? null,
      holdoutPct,
      effectiveSendAt,
      selection,
      phones: selectedMembers.map((m) => m.customer_phone),
    });
    setConfirmOpen(false);
    onBack();
  };

  return (
    <div className="space-y-4">
      <KillSwitchBanner />

      <div className="flex items-center gap-3">
        <Button type="button" variant="ghost" className="h-11 px-3" onClick={onBack}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back
        </Button>
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            {promotion ? `Send "${promotion.title}"` : "Send a campaign"}
          </h2>
          <p className="text-sm text-muted-foreground">Campaign → Send</p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-2 p-4 sm:p-6">
              <Label htmlFor="composer-promotion">Promotion</Label>
              <p className="text-xs leading-relaxed text-muted-foreground">
                The offer this text carries. Its claim page opens from the link in the message.
              </p>
              <PromotionSelect promotions={promotions} value={promotionId} onChange={setPromotionId} />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-2 p-4 sm:p-6">
              <Label htmlFor="composer-message">Message</Label>
              <p className="text-xs leading-relaxed text-muted-foreground">
                This is the text customers receive. <code className="rounded bg-muted px-1">{"{offer_link}"}</code>{" "}
                becomes each customer's personal claim link. Include opt-out language (e.g. "Reply STOP to opt out").
              </p>
              <Textarea
                id="composer-message"
                className="text-base"
                rows={4}
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
              />
              {meter && (
                <SmsCostMeter
                  meter={meter}
                  audienceCount={textedCount}
                  costPerSegment={meterSettings!.sms_cost_per_segment}
                />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-2 p-4 sm:p-6">
              <Label>Audience</Label>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Only people who joined your text club can be texted. Counts are live.
              </p>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Audience">
                {AUDIENCE_CHIPS.map((c) => {
                  const count = countFor(c.key);
                  const selected = c.key === audienceKey;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setAudienceKey(c.key)}
                      className={`h-11 rounded-full border px-4 text-sm font-medium transition-colors ${
                        selected
                          ? "border-status-info bg-status-info/10 text-status-info"
                          : "border-border bg-card text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {c.label}
                      {c.days ? ` (${c.days})` : ""} · {count === null ? "…" : count}
                    </button>
                  );
                })}
              </div>
              <p className="text-sm font-medium text-foreground">
                {breakdownLoading ? "Estimating recipients…" : countsLine}
              </p>
              {breakdown && contactCounts && (
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {breakdown.counts.everyone} subscriber{breakdown.counts.everyone === 1 ? "" : "s"} today, out of{" "}
                  {contactCounts.total_customers.toLocaleString()} customers. Order Invite grows this number daily.
                </p>
              )}
            </CardContent>
          </Card>

          <TestSendCard
            messageBody={messageBody}
            promotionId={promotion?.id ?? null}
            imageUrl={promotion?.image_url ?? null}
            expirationDays={promotion?.default_expiration_days ?? null}
            timerMinutes={promotion?.default_timer_minutes ?? null}
            ownerTestPhone={marketingSettings?.owner_test_phone ?? null}
          />

          <Card>
            <CardContent className="grid gap-4 p-4 sm:grid-cols-2 sm:p-6">
              <div className="space-y-1.5">
                <Label htmlFor="composer-send-mode">Send</Label>
                <Select value={sendMode} onValueChange={(v) => setSendMode(v as typeof sendMode)}>
                  <SelectTrigger id="composer-send-mode" className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">Immediately</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                  </SelectContent>
                </Select>
                {sendMode === "scheduled" && (
                  <Input
                    type="datetime-local"
                    aria-label="Scheduled send time"
                    className="h-11"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                  />
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="composer-holdout">Holdout %</Label>
                <Input
                  id="composer-holdout"
                  type="number"
                  min={0}
                  max={100}
                  className="h-11"
                  value={holdoutPct}
                  onChange={(e) => setHoldoutPct(parseInt(e.target.value, 10) || 0)}
                />
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Held back for comparison, they never receive this campaign.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              type="button"
              className="h-11"
              disabled={!canReview}
              onClick={() => setConfirmOpen(true)}
            >
              Review &amp; send to {textedCount} people
            </Button>
          </div>
        </div>

        <div className="lg:pt-2">
          <IphonePreview
            body={previewBody}
            imageUrl={promotion?.image_url}
            storeName={marketingSettings?.store_name ?? "Your store"}
            storePhone={marketingSettings?.store_phone ?? null}
          />
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send this campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              "{promotion?.title}" will be sent to <strong>{audienceLabel}</strong> — <strong>{countsLine}</strong>.{" "}
              {sendMode === "immediate"
                ? "Sending starts as soon as the next send cycle runs."
                : "It is scheduled, not sent immediately."}
            </AlertDialogDescription>
            {marketingSettings?.kill_switch && (
              <AlertDialogDescription className="font-medium text-status-danger">
                Sending is currently paused platform-wide (emergency stop is on). This campaign will be created and
                scheduled, but no text will go out until the emergency stop is turned off in Settings.
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction disabled={createCampaign.isPending || breakdownLoading} onClick={launch}>
              Send campaign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
